import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Popover from "antd/es/popover";
import Row from "antd/es/row";
import Select from "antd/es/select";
import { endOfDay, endOfYesterday, isAfter, startOfDay } from "date-fns/esm";
import { FormattedMessage, useIntl } from "react-intl";

import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import RangePicker from "@app/component/input/RangePicker";
import SelectField from "@app/component/input/SelectField";
import ReportType from "@app/enum/ReportType";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";

import DateRangeType from "../../enums/DateRangeType";
import { getAnalyticIssuerByTabKey } from "../../reportsAnalytics";
import { setDateRange, setDateRangeType } from "../../store/actions";
import { getDateRange, getDateRangeType } from "../../store/selectors";
import DateRange from "../../types/DateRange";

import styles from "./TimeSelector.module.scss";

export const futureDateDisabled = (date: Date) => isAfter(date, endOfYesterday());

interface Props {
  dateRange: DateRange;
  dateRangeType: DateRangeType;
  isDateDisabled?: (date: Date) => boolean;
  onDateRangeChange: (dateRange: DateRange) => void;
  onDateRangeTypeChange: (dateRangeType: DateRangeType) => void;
  onlyCustom?: boolean;
  reportType: ReportType;
  tooltipText?: React.ReactNode;
}

const TimeSelector: React.FC<Props> = ({
  dateRange,
  dateRangeType,
  isDateDisabled = futureDateDisabled,
  onDateRangeChange,
  onDateRangeTypeChange,
  onlyCustom,
  reportType,
}) => {
  const intl = useIntl();

  const [popoverVisible, showPopover, hidePopover] = useBoolean();
  const [newDateRangeType, setNewDateRangeType] = useState<DateRangeType>(dateRangeType);
  const [newDateRange, setNewDateRange] = useState<DateRange>(dateRange);

  const isCustomRangePickerVisible = newDateRangeType === DateRangeType.CUSTOM;

  useEffect(() => {
    setNewDateRange(dateRange);
  }, [dateRange, setNewDateRange]);

  useEffect(() => {
    setNewDateRangeType(dateRangeType);
  }, [dateRangeType, setNewDateRangeType]);

  useEffect(() => {
    if (onlyCustom) {
      setNewDateRangeType(DateRangeType.CUSTOM);
    }
  }, [onlyCustom, setNewDateRangeType]);

  const handleDateRangeTypeChange = useCallback(
    (value: DateRangeType) => {
      getAnalyticIssuerByTabKey(reportType).clicked(["Select Range", value]);
      setNewDateRangeType(value);
    },
    [reportType, setNewDateRangeType]
  );

  const handleDateRangeChange = useCallback(
    ([startDate, endDate]) => {
      if (startDate && endDate) {
        setNewDateRange({ endDate: endOfDay(endDate), startDate: startOfDay(startDate) });
        getAnalyticIssuerByTabKey(reportType).clicked(["Change Date"], { endDate, startDate });
      }
    },
    [reportType, setNewDateRange]
  );

  const handleApply = useCallback(() => {
    if (newDateRangeType !== DateRangeType.CUSTOM) {
      onDateRangeTypeChange?.(newDateRangeType);
    } else {
      onDateRangeChange?.(newDateRange);
    }
    hidePopover();
  }, [onDateRangeTypeChange, onDateRangeChange, newDateRange, newDateRangeType, hidePopover]);

  const handleCancel = useCallback(() => {
    hidePopover();
    setNewDateRange(dateRange);
    setNewDateRangeType(dateRangeType);
  }, [dateRange, dateRangeType, hidePopover]);

  return (
    <Popover
      content={
        <div>
          <div className={styles.title}>
            {intl.formatMessage({
              id: "timeselector",
              defaultMessage: "Change date range",
              description: "Reports date range selector",
            })}
          </div>
          <Row gutter={[layout.spacerS, layout.spacerM]}>
            <Col span={24}>
              <SelectField<DateRangeType>
                className={styles.select}
                disabled={onlyCustom}
                label={intl.formatMessage({
                  id: "timeselector.timespan",
                  defaultMessage: "Timespan",
                  description: "Reports timespan field select label",
                })}
                onChange={handleDateRangeTypeChange}
                value={newDateRangeType}
              >
                <Select.Option value={DateRangeType.YESTERDAY}>
                  <FormattedMessage
                    id="reports.header.timelineBar.yesterday"
                    defaultMessage="Yesterday"
                    description="Yesterday Radio Button in Reports header"
                  />
                </Select.Option>
                <Select.Option value={DateRangeType.THIS_WEEK}>
                  <FormattedMessage
                    id="reports.header.timeSelector.currentWeek"
                    defaultMessage="Current Week (Sunday - Saturday)"
                    description="Last Week Radio Button in Reports header"
                  />
                </Select.Option>
                <Select.Option value={DateRangeType.LAST_WEEK}>
                  <FormattedMessage
                    id="reports.header.timeSelector.previousWeek"
                    defaultMessage="Previous Week (Sunday - Saturday)"
                    description="Last Week Radio Button in Reports header"
                  />
                </Select.Option>
                <Select.Option value={DateRangeType.THIS_MONTH}>
                  <FormattedMessage
                    id="reports.header.timeSelector.currentMonth"
                    defaultMessage="Current Month"
                    description="Last Week Radio Button in Reports header"
                  />
                </Select.Option>
                <Select.Option value={DateRangeType.PREVIOUS_MONTH}>
                  <FormattedMessage
                    id="reports.header.timeSelector.previousMonth"
                    defaultMessage="Previous Month"
                    description="Last Week Radio Button in Reports header"
                  />
                </Select.Option>
                <Select.Option value={DateRangeType.CUSTOM}>
                  <FormattedMessage
                    id="reports.header.timeSelector.custom"
                    defaultMessage="Custom"
                    description="Last Week Radio Button in Reports header"
                  />
                </Select.Option>
              </SelectField>
            </Col>
            {isCustomRangePickerVisible ? (
              <Col span={24}>
                <div>
                  {intl.formatMessage({
                    id: "timeselector.custom.selector.label",
                    defaultMessage: "Select range",
                    description: "Reports custom selector select label",
                  })}
                </div>
                <RangePicker
                  className={styles.datePicker}
                  disabledDate={isDateDisabled}
                  onChange={handleDateRangeChange}
                  value={[newDateRange?.startDate ?? null, newDateRange?.endDate ?? null]}
                />
              </Col>
            ) : null}
            <Col span={24}>
              <Row gutter={layout.spacerS} justify="end">
                <Col>
                  <Button onClick={handleCancel}>
                    {intl.formatMessage({
                      id: "activityList.sort.cancel",
                      defaultMessage: "Cancel",
                      description: "Activities list sort select cancel",
                    })}
                  </Button>
                </Col>
                <Col>
                  <Button onClick={handleApply} type="primary">
                    {intl.formatMessage({
                      id: "activityList.sort.apply",
                      defaultMessage: "Apply",
                      description: "Activities list sort select apply",
                    })}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      }
      open={popoverVisible}
    >
      <Button className={styles.changeButton} onClick={showPopover} type="link">
        {intl.formatMessage({
          id: "reports.timeSelector.change.button",
          defaultMessage: "Change",
          description: "Time selector button for date change on reports",
        })}
      </Button>
    </Popover>
  );
};

const mapStateToProps = (state: RootState) => ({
  dateRange: getDateRange(state),
  dateRangeType: getDateRangeType(state),
});

const mapDispatchToProps = {
  onDateRangeChange: setDateRange,
  onDateRangeTypeChange: setDateRangeType,
};

export default connect(mapStateToProps, mapDispatchToProps)(TimeSelector);
